import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faEye } from '@fortawesome/free-solid-svg-icons';

import { useCreatePack } from '../../hooks/useCreatePack';
import PackInformationForm from './components/CreatePackModalComponents/PackInformationForm';
import PackFileUploadManager from './components/CreatePackModalComponents/PackFileUploadManager';
import CoverImagePreview from '../../components/common/CoverImagePreview';
import ContentOptionsForm from '../../components/content/ContentOptionsForm';
import PremiumContentOptionsForm from '../../components/content/PremiumContentOptionsForm';
import FormToggle from '../../components/common/FormToggle';
import EffectSelectorModal from '../Packs/components/PackEditMediaComponents/EffectSelectorModal';

const CreatePackModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [isUserView, setIsUserView] = useState(false);

  const { state, handlers } = useCreatePack({ onClose, onSuccess });
  const {
    formData,
    items,
    coverIndex,
    customCoverFile,
    customCoverEffect, // <-- Nueva info del hook
    temporaryCoverPreviewUrl, // <-- Nueva info del hook
    error,
    isPending,
    loadingEffects,
    isEffectModalOpen,
    modalPreviewState,
    isPreviewing,
    effectsData,
  } = state;
  const {
    updateField,
    updatePrice,
    addFiles,
    removeFile,
    setContentCover,
    handleCoverFileChange,
    handleRemoveCover,
    openEffectModal,
    handleModalEffectSelect,
    handleModalIntensityChange,
    confirmItemEffect,
    setIsEffectModalOpen,
    getDisplayUrlForModal,
    handleSubmit,
  } = handlers;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70 backdrop-blur-sm p-4 animate-fadeIn">
        <div
          className="max-w-2xl w-full mx-auto bg-white dark:bg-neutral-900 rounded-xl shadow-card flex flex-col max-h-[95vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-4 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
            <h2 className="text-xl font-bold font-display text-neutral-900 dark:text-white">
              {t('packs.create_modal.title')}
            </h2>
            <button
              onClick={onClose}
              disabled={isPending}
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-700 transition"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-grow flex flex-col overflow-hidden"
          >
            <div className="flex-grow overflow-y-auto p-6 space-y-6">
              {error && (
                <div className="p-3 rounded-lg text-red-700 bg-red-50 dark:bg-red-500/10 dark:text-red-300 border border-red-200 dark:border-red-500/30 text-sm">
                  {error}
                </div>
              )}

              <PackInformationForm
                name={formData.name}
                description={formData.description}
                onChange={updateField}
                disabled={isPending}
              />

              {formData.isPremium && (
                <div className="p-3 rounded-lg bg-primary/5 dark:bg-primary/10 border border-primary/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faEye} className="text-primary" />
                    <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                      {t(
                        'posts.create_modal.user_preview_toggle_label',
                        'Vista de Usuario'
                      )}
                    </span>
                  </div>
                  <FormToggle
                    name="user_view"
                    checked={isUserView}
                    onChange={() => setIsUserView((prev) => !prev)}
                  />
                </div>
              )}

              <PackFileUploadManager
                items={items}
                coverIndex={coverIndex}
                hasCustomCover={!!customCoverFile}
                isPremium={formData.isPremium}
                disabled={isPending}
                onAddFiles={addFiles}
                onRemoveFile={removeFile}
                onSetContentCover={setContentCover}
                onEditItemEffect={(item) =>
                  openEffectModal({ type: 'item', data: item })
                }
                isUserView={isUserView}
              />

              <CoverImagePreview
                stagedFile={customCoverFile}
                onFileSelect={handleCoverFileChange}
                onFileRemove={handleRemoveCover}
                label={t(
                  'packEdit.coverImage.label',
                  'Portada Personalizada (Opcional)'
                )}
                description={t(
                  'packEdit.coverImage.descriptionCreate',
                  'Sube una imagen específica para la portada. Si no subes una, la portada será el primer item de la galería.'
                )}
                disabled={isPending}
                isPremium={formData.isPremium}
                onEditEffect={() => openEffectModal({ type: 'customCover' })}
                // --- INICIO DE LA CORRECCIÓN ---
                // Se pasan las nuevas props del hook al componente
                isUserView={isUserView}
                coverFileEffect={customCoverEffect}
                temporaryPreviewUrl={temporaryCoverPreviewUrl}
                // --- FIN DE LA CORRECCIÓN ---
              />

              <div className="space-y-4 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
                <ContentOptionsForm
                  isPublic={formData.isPublic}
                  isPremium={formData.isPremium}
                  onChange={updateField}
                  disabled={isPending}
                />
                <hr className="border-neutral-200 dark:border-neutral-700" />
                <FormToggle
                  label={t('common.enable_comments', 'Habilitar comentarios')}
                  name="commentsEnabled"
                  checked={formData.commentsEnabled}
                  onChange={updateField}
                  disabled={isPending}
                />
              </div>

              {formData.isPremium && (
                <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
                  <PremiumContentOptionsForm
                    price={formData.price}
                    onPriceChange={updatePrice}
                    disabled={isPending || loadingEffects}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-neutral-200 dark:border-neutral-700 flex-shrink-0">
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={isPending}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary min-w-[140px]"
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    {t('common.creating', 'Creando...')}
                  </>
                ) : (
                  t('packs.create_modal.create_button', 'Crear Galería')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {isEffectModalOpen && (
        <EffectSelectorModal
          isOpen={isEffectModalOpen}
          onClose={() => setIsEffectModalOpen(false)}
          displayImage={getDisplayUrlForModal()}
          availableEffects={effectsData}
          onSelectEffect={handleModalEffectSelect}
          onConfirm={confirmItemEffect}
          isPreviewing={isPreviewing}
          previewState={modalPreviewState}
          onIntensityChange={handleModalIntensityChange}
        />
      )}
    </>
  );
};

export default CreatePackModal;
